import React, { useState, useRef, useEffect, useCallback } from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plus, ArrowLeft, ArrowUpDown, ChevronDown, Check, ChevronLeft, ChevronRight } from "lucide-react";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import { useNavigate } from "react-router-dom";
import { ProjectTypeSelector } from "../ProjectTypeSelector";
import { SaveStatus } from "@/hooks/domains/portfolio/usePortfolioAutosave";
import { SaveStatusIndicator } from "../SaveStatusIndicator";

interface PortfolioHeaderProps {
  hasItems: boolean;
  isAdding: boolean;
  isEditing: boolean;
  onAddClick: () => void;
  onBackClick?: () => void;
  maxReached?: boolean;
  isProcessing?: boolean;
  rightAction?: React.ReactNode;
  onSelectShot?: () => void;
  onSelectCaseStudy?: () => void;
  // New props for filtering and sorting
  activeFilter: 'all' | 'case_studies' | 'shots' | 'portfolio_layout';
  onFilterChange: (filter: 'all' | 'case_studies' | 'shots' | 'portfolio_layout') => void;
  sortOrder: 'last_added' | 'alphabetical' | 'featured_first' | 'most_viewed';
  onSortChange: (sort: 'last_added' | 'alphabetical' | 'featured_first' | 'most_viewed') => void;
  // Counts for display
  allCount: number;
  caseStudiesCount: number;
  shotsCount: number;
  // Autosave status
  saveStatus?: SaveStatus;
  showAutosaveIndicator?: boolean;
}

const PortfolioHeader = ({
  hasItems,
  isAdding,
  isEditing,
  onAddClick,
  onBackClick,
  maxReached = false,
  isProcessing = false,
  rightAction,
  onSelectShot,
  onSelectCaseStudy,
  activeFilter,
  onFilterChange,
  sortOrder,
  onSortChange,
  allCount,
  caseStudiesCount,
  shotsCount,
  saveStatus,
  showAutosaveIndicator = false,
}: PortfolioHeaderProps) => {
  const [showProjectTypeSelector, setShowProjectTypeSelector] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const sortDropdownRef = useRef<HTMLDivElement>(null);
  const tabsScrollRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const navigate = useNavigate();

  // Check scroll position to show/hide tab scroll arrows
  const checkTabsScrollPosition = useCallback(() => {
    const container = tabsScrollRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const hasOverflow = scrollWidth > clientWidth;

    setShowLeftArrow(hasOverflow && scrollLeft > 0);
    setShowRightArrow(hasOverflow && scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  // Initial check and resize listener for tabs scroll
  useEffect(() => {
    checkTabsScrollPosition();
    window.addEventListener("resize", checkTabsScrollPosition);
    return () => window.removeEventListener("resize", checkTabsScrollPosition);
  }, [checkTabsScrollPosition]);

  // Scroll handlers for tabs
  const scrollTabsLeft = () => {
    const container = tabsScrollRef.current;
    if (container) {
      container.scrollBy({ left: -150, behavior: "smooth" });
    }
  };

  const scrollTabsRight = () => {
    const container = tabsScrollRef.current;
    if (container) {
      container.scrollBy({ left: 150, behavior: "smooth" });
    }
  };

  const title = isAdding
    ? "Create New Showcase Project"
    : isEditing
    ? "Customize Your Project"
    : "My Project Portfolio";

  const handleAddProject = () => {
    if (onSelectShot && onSelectCaseStudy) {
      setShowProjectTypeSelector(true);
    } else {
      onAddClick();
    }
  };

  const handleSelectShot = () => {
    setShowProjectTypeSelector(false);
    onSelectShot?.();
  };

  const handleSelectCaseStudy = () => {
    setShowProjectTypeSelector(false);
    onSelectCaseStudy?.();
  };

  // Handle clicking outside the sort dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target as Node)) {
        setShowSortDropdown(false);
      }
    };

    if (showSortDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSortDropdown]);

  const sortOptions = [
    { value: 'last_added', label: 'Last Added' },
    { value: 'last_edited', label: 'Last Edited' },
    { value: 'alphabetical', label: 'Alphabetical (A-Z)' },
    { value: 'featured_first', label: 'Featured First' },
    { value: 'most_viewed', label: 'Most Viewed' }
  ];

  const currentSortLabel = sortOptions.find(option => option.value === sortOrder)?.label || 'Last Added';

  // Don't show the new header when adding or editing
  if (isAdding || isEditing) {
    return (
      <div
        className={`flex relative z-20 bg-white py-2 md:px-4 ${
          rightAction
            ? 'flex-col items-start md:flex-row md:items-center md:justify-between'
            : 'flex-row items-center justify-between'
        }`}
      >
        <div className="flex items-center gap-3">
          {onBackClick && (
            <ArrowLeft
              className="h-4 w-4 text-muted-foreground cursor-pointer"
              onClick={onBackClick}
            />
          )}
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">
              {isAdding ? "Create New Showcase Project" : "Customize Your Project"}
            </h1>
            {/* Autosave status indicator */}
            {showAutosaveIndicator && saveStatus && (
              <SaveStatusIndicator status={saveStatus} />
            )}
          </div>
        </div>
        {rightAction && (
          <div className="mt-3 w-full md:mt-0 md:w-auto md:ml-4">{rightAction}</div>
        )}
      </div>
    );
  }

  return (
    <div className="relative z-20 bg-white py-2 md:px-0" style={{ 
      contain: 'layout style',
      willChange: 'auto',
      transform: 'translateZ(0)'
  }}>
      {/* Portfolio Title */}
      <div className="mb-4">
        <h1 className="text-base font-medium">Portfolio</h1>
        {maxReached && (
          <span className="text-xs text-muted-foreground mt-1">
            Maximum of 6 projects reached
          </span>
        )}
      </div>

      {/* Filter Tabs and Controls */}
      <div className="flex items-center justify-between bg-white p-3 rounded-lg shadow-[inset_0_2px_4px_rgba(0,0,0,0.05),inset_0_-2px_4px_rgba(0,0,0,0.05),inset_2px_0_4px_rgba(0,0,0,0.05),inset_-2px_0_4px_rgba(0,0,0,0.05)] overflow-visible" style={{
        contain: 'layout style',
        willChange: 'auto'
      }}>
        {/* Filter Tabs with scroll */}
        <div className="flex items-center flex-1 min-w-0 mr-2">
          {/* Left scroll indicator - outside scrollable area */}
          {showLeftArrow && (
            <button
              onClick={scrollTabsLeft}
              className="flex-shrink-0 flex items-center justify-center w-8 h-8 mr-1 rounded-md border border-gray-200 bg-white hover:bg-gray-50 transition-all"
              aria-label="Scroll left"
            >
              <ChevronLeft className="h-4 w-4 text-gray-500" />
            </button>
          )}

          {/* Scrollable tabs container */}
          <div
            ref={tabsScrollRef}
            onScroll={checkTabsScrollPosition}
            className="flex items-center gap-1 overflow-x-auto scrollbar-hide scroll-smooth flex-1 min-w-0"
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onFilterChange('all')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all flex-shrink-0 ${
                activeFilter === 'all'
                  ? 'bg-white text-black shadow-md border border-gray-200'
                  : 'text-[#737373] font-medium'
              }`}
            >
              All ({allCount})
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onFilterChange('case_studies')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all flex-shrink-0 whitespace-nowrap ${
                activeFilter === 'case_studies'
                  ? 'bg-white text-black shadow-md border border-gray-200'
                  : 'text-[#737373] font-medium'
              }`}
            >
              Case Studies ({caseStudiesCount})
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onFilterChange('shots')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all flex-shrink-0 ${
                activeFilter === 'shots'
                  ? 'bg-white text-black shadow-md border border-gray-200'
                  : 'text-[#737373] font-medium'
              }`}
            >
              Shots ({shotsCount})
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onFilterChange('portfolio_layout')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all flex-shrink-0 whitespace-nowrap ${
                activeFilter === 'portfolio_layout'
                  ? 'bg-white text-black shadow-md border border-gray-200'
                  : 'text-[#737373] font-medium'
              }`}
            >
              Portfolio Layout
            </Button>
          </div>

          {/* Right scroll indicator - outside scrollable area */}
          {showRightArrow && (
            <button
              onClick={scrollTabsRight}
              className="flex-shrink-0 flex items-center justify-center w-8 h-8 ml-1 rounded-md border border-gray-200 bg-white hover:bg-gray-50 transition-all"
              aria-label="Scroll right"
            >
              <ChevronRight className="h-4 w-4 text-gray-500" />
            </button>
          )}
        </div>

        {/* Sort Dropdown and Add Project Button */}
        <div className="flex items-center gap-3 relative" style={{
          contain: 'layout style',
          willChange: 'auto'
        }}>
          {/* Custom Sort Dropdown - Hidden when Portfolio Layout tab is active */}
          {activeFilter !== 'portfolio_layout' && (
            <div className="relative hidden md:block" ref={sortDropdownRef}>
            <Button
              variant="outline"
              className="w-[190px] h-8 px-3 py-1 text-sm border-gray-200 bg-white hover:border-black hover:bg-white hover:text-black flex items-center gap-2 justify-between"
              onClick={() => setShowSortDropdown(!showSortDropdown)}
            >
              <div className="flex items-center gap-2">
                <ArrowUpDown className="h-4 w-4 text-gray-500" />
                <span>{currentSortLabel}</span>
              </div>
              <ChevronDown className="h-4 w-4 text-gray-500" />
            </Button>
            
            {showSortDropdown && (
              <div 
                className="absolute top-full right-0 mt-1 w-[180px] bg-white border border-gray-200 rounded-lg shadow-md z-50 p-1"
                style={{
                  position: 'absolute',
                  transform: 'translateZ(0)',
                  willChange: 'transform'
                }}
              >
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    className={`w-full text-left px-3 py-2 text-sm text-gray-900 hover:bg-gray-100 hover:rounded-lg first:rounded-lg last:rounded-lg flex items-center gap-2 ${
                      sortOrder === option.value ? 'bg-gray-100 rounded-lg' : ''
                    }`}
                    onClick={() => {
                      onSortChange(option.value as 'last_added' | 'alphabetical' | 'featured_first' | 'most_viewed');
                      setShowSortDropdown(false);
                    }}
                  >
                    {sortOrder === option.value && (
                      <Check className="h-4 w-4 text-gray-900" />
                    )}
                    <span className={sortOrder === option.value ? 'font-medium' : ''}>
                      {option.label}
                    </span>
                  </button>
                ))}
              </div>
            )}
            </div>
          )}

          {/* Add Project Button */}
          {!maxReached && (
            <div className="hidden md:block">
              <Popover open={showProjectTypeSelector} onOpenChange={setShowProjectTypeSelector}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleAddProject}
                  className="flex items-center gap-2 bg-black text-white hover:bg-gray-800 px-3 py-1.5 h-8"
                  disabled={isProcessing}
                >
                  <Plus className="h-4 w-4" />
                  Add Project
                </Button>
              </PopoverTrigger>
              <ProjectTypeSelector
                open={showProjectTypeSelector}
                onOpenChange={setShowProjectTypeSelector}
                onSelectShot={handleSelectShot}
                onSelectCaseStudy={handleSelectCaseStudy}
              />
            </Popover>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PortfolioHeader;
